/** @jsxRuntime classic */
/** @jsx jsx */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { jsx } from "@emotion/core";
import { convertToRaw, EditorState, Modifier, RichUtils, SelectionState } from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import createMentionPlugin, {
  defaultSuggestionsFilter,
} from '@draft-js-plugins/mention';
import createInlineToolbarPlugin from '@draft-js-plugins/inline-toolbar';
import createLinkPlugin from '@draft-js-plugins/anchor';
import createLinkifyPlugin from '@draft-js-plugins/linkify';
import '@draft-js-plugins/inline-toolbar/lib/plugin.css';
import '@draft-js-plugins/mention/lib/plugin.css';
import '@draft-js-plugins/anchor/lib/plugin.css';
import _ from 'lodash';
import { stateFromHTML } from 'draft-js-import-html';
import {
  ItalicButton,
  BoldButton,
  UnderlineButton,
  UnorderedListButton,
  OrderedListButton,
} from '@draft-js-plugins/buttons';
import { getScore, hasChildTextNode, parseTodoistTextToHTML } from '../action/utilities';
import { Trash } from '../LandingHeader/Feedback/icons/Icons';
import Check from '../DatePicker/components/Check';

const linkPlugin = createLinkPlugin({linkTarget: '_blank'});
const descriptionLinkPlugin = createLinkPlugin({linkTarget: '_blank'});
const linkifyPlugin = createLinkifyPlugin({target: '_blank'});
const descriptionLinkifyPlugin = createLinkifyPlugin({target: '_blank'});

function Entry(props) {
  const {
    mention,
    theme,
    searchValue, 
    isFocused,
    ...parentProps
  } = props;
 
  return (
    <div {...parentProps}>
      <div className={theme?.mentionSuggestionsEntryContainer}>
        <div className={theme?.mentionSuggestionsEntryTitle}>
          {mention?.new === true && mention?.trigger === '@' ? `+ Create "${mention.name}"` : mention.name}
        </div>
      </div>
    </div>
  );
}

const MediumLikeEditorWithMentions = forwardRef(({id = 'inputField', projects = [], taskTitle = '', taskDescription = '', handleKeyPress, setFocus, currentPage, addTaskOnupComingFocus, onDateChage, addTaskOnFocus, classNames = '', selectedProject = null, onHide = null, onChange = () => {}}, ref) => {
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  useImperativeHandle(ref, () => ({
    title: titleRef.current,
    description: descriptionRef.current
  }));
  const [open, setOpen] = useState(false);
  const mentions = projects?.map(project => ({name: project?.value, display: project?.label})) ?? [];
  const [suggestions, setSuggestions] = useState([]);
  const [popup, setPopup] = useState(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const popupElement = document.querySelector('.edit-link-popup');
      if (popupElement && !popupElement.contains(event.target)) {
        setPopup(null);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Task title plugins
  const { MentionSuggestions, InlineToolbar, plugins } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      mentionTrigger: ['@'],
      mentionSuggestions: [],
      mentionPrefix: (trigger) => trigger,
      theme: {
        mention: 'mention',
        mentionSuggestions: 'mentionSuggestions',
        mentionSuggestionsEntry: 'mentionSuggestionsEntry',
        mentionSuggestionsEntryFocused: 'mentionSuggestionsEntryFocused',
        mentionSuggestionsEntryText: 'mentionSuggestionsEntryText',
        mentionSuggestionsEntryTitle: 'mentionSuggestionsEntryTitle',
        mentionSuggestionsEntryContainer: 'mentionSuggestionsEntryContainer'
      },
      mentionComponent: (mentionProps) => (
        <span
          className={mentionProps.className}
          style={{ 
            backgroundColor: `var(--accent-color)`,
            color: '#fff'
          }}
        >
          {mentionProps.children}
        </span>
      ),
    });
    const inlineToolbarPlugin = createInlineToolbarPlugin({
      structure: [linkPlugin.LinkButton],
    })
    const eventPlugin = {
      keyBindingFn: (e) => {
        if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          e.preventDefault();
          return 'not-handled';
        }
        // Add key bindings for formatting shortcuts
        if (e.ctrlKey || e.metaKey) {
          if (e.key === 'b') {
            return 'bold';
          }
          if (e.key === 'i') {
            return 'italic';
          }
          if (e.key === 'u') {
            return 'underline';
          }
        }
      }
    };
    // eslint-disable-next-line no-shadow
    const { MentionSuggestions } = mentionPlugin;
    // eslint-disable-next-line no-shadow
    const plugins = [mentionPlugin, inlineToolbarPlugin, linkPlugin, linkifyPlugin, eventPlugin];
    return { plugins, MentionSuggestions, InlineToolbar: inlineToolbarPlugin.InlineToolbar };
  }, []);

  // Task description plugins
  const { DescriptionInlineToolbar, descriptionPlugins } = useMemo(() => {
    const inlineToolbarPlugin = createInlineToolbarPlugin();
    const eventPlugin = {
      keyBindingFn: (e, editorState) => {
        const selection = editorState.getEditorState().getSelection();
        const currentContent = editorState.getEditorState().getCurrentContent();
        const blockKey = selection.getStartKey();
        const block = currentContent.getBlockForKey(blockKey);
        const blockText = block.getText();
        const blockLength = blockText.length;

        if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
          e.preventDefault();
          return 'not-handled';
        }
        // Add key bindings for formatting shortcuts
        if (e.ctrlKey || e.metaKey) {
          if (e.key === 'b') {
            return 'bold';
          }
          if (e.key === 'i') {
            return 'italic';
          }
          if (e.key === 'u') {
            return 'underline';
          }
        }
        // Convert "-" to unordered list only at the start of a new line
        if (blockLength === 0 || selection.getAnchorOffset() === 0) {
          if (e.key === '-') {
            return 'unordered-list';
          }
        }
        // Convert "1." to ordered list only at the start of a new line
        if (blockLength === 1 || selection.getAnchorOffset() === 1) {
          if (e.key === '.' && blockText === '1') {
            setTimeout(() => {
              // Clear the block text after converting to ordered list
            const contentState = editorState.getEditorState().getCurrentContent();
            const selection = editorState.getEditorState().getSelection();
            const newContentState = Modifier.replaceText(
              contentState,
              selection.merge({
                anchorOffset: 0,
                focusOffset: blockText.length
              }),
              ''
            );
            editorState.setEditorState(EditorState.push(
              editorState.getEditorState(),
              newContentState,
              'remove-range'
            ));
            }, 0);
            return 'ordered-list';
          }
        }
      }
    };
    // eslint-disable-next-line no-shadow
    const plugins = [inlineToolbarPlugin, descriptionLinkPlugin, descriptionLinkifyPlugin, eventPlugin];
    return { descriptionPlugins: plugins, DescriptionInlineToolbar: inlineToolbarPlugin.InlineToolbar };
  }, []);

  const addMentionsToContent = (contentState, blocks) => {
    let contentStateWithMentions = contentState;

    blocks.forEach(block => {
      const blockText = block.getText();
      const words = blockText.split(' ');
      //console.log('blockText', blockText);
      [blockText].forEach((word, index) => {
        if (word.startsWith('@')) {
          const mentionName = word.slice(1);
          const mention = mentions.find(m => m.name === mentionName);

          if (mention) {
            // Calculate the start and end positions of the mention in the block
            const start = blockText.indexOf(word);
            const end = start + word.length;

            // Create a mention entity
            const contentStateWithEntity = contentStateWithMentions.createEntity(
              'mention',
              'IMMUTABLE',
              { mention }
            );
            const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

            // Create a selection for the mention text
            const selection = SelectionState.createEmpty(block.getKey()).merge({
              anchorOffset: start,
              focusOffset: end,
            });

            // Apply the entity to the selected text
            contentStateWithMentions = Modifier.applyEntity(
              contentStateWithEntity,
              selection,
              entityKey
            );
          }
        }
      });
    });

    return contentStateWithMentions;
  };

  const [titleEditorState, setTitleEditorState] = useState(() => {
    if (taskTitle) {
      let taskTitleText = parseTodoistTextToHTML(taskTitle);
      const hasMatch = taskTitleText.match(/@\[(\w+)\]/);
      taskTitleText = hasMatch ? taskTitleText.replace(/@\[(\w+)\]/g, '@$1') : taskTitleText;
      const contentState = stateFromHTML(taskTitleText);
      if (contentState) {
        const blocks = contentState.getBlocksAsArray();
        const contentStateWithMentions = addMentionsToContent(contentState, blocks);
        return EditorState.createWithContent(contentStateWithMentions);
      }
    }
    return EditorState.createEmpty();
  });
  
  const [descriptionEditorState, setDescriptionEditorState] = useState(() => {
    if (taskDescription) {
      const contentState = stateFromHTML(parseTodoistTextToHTML(taskDescription));
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  });

  // Simple focus effect
  useEffect(() => {
    if (titleRef?.current) {
      setTimeout(() => {
        titleRef.current.focus();
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (selectedProject !== undefined && selectedProject !== '@') {
      // Get current content
      let currentContent = titleEditorState.getCurrentContent();
      
      // Remove all existing mentions
      const blocks = currentContent.getBlocksAsArray();
      blocks.forEach(block => {
        const entityRanges = [];
        
        block.findEntityRanges(
          character => {
            const entityKey = character.getEntity();
            return entityKey !== null && currentContent.getEntity(entityKey).getType() === 'mention';
          },
          (start, end) => entityRanges.push({start, end})
        );

        // Remove mentions in reverse order
        entityRanges.reverse().forEach(({start, end}) => {
          const selection = SelectionState.createEmpty(block.getKey()).merge({
            anchorOffset: start,
            focusOffset: end + 1 // Extra 1 for space
          });
          currentContent = Modifier.removeRange(currentContent, selection, 'backward');
        });
      });

      // Add new mention if selectedProject exists
      if (selectedProject) {
        const lastBlock = currentContent.getLastBlock();
        // Create and apply mention entity
        const contentWithEntity = currentContent.createEntity(
          'mention',
          'IMMUTABLE',
          { mention: {name: selectedProject, display: selectedProject} }
        );

        const mentionText = '@' + selectedProject;
        const mentionSelection = SelectionState.createEmpty(lastBlock.getKey()).merge({
          anchorOffset: lastBlock.getLength(),
          focusOffset: lastBlock.getLength()
        });

        // Add space at the end of mention
        currentContent = Modifier.insertText(contentWithEntity, mentionSelection, mentionText + ' ');
        
        const entityKey = contentWithEntity.getLastCreatedEntityKey();
        const selectionForEntity = mentionSelection.merge({
          focusOffset: mentionSelection.getFocusOffset() + mentionText.length
        });

        currentContent = Modifier.applyEntity(currentContent, selectionForEntity, entityKey);
      }

      // Insert Mention
      setTitleEditorState(EditorState.push(titleEditorState, currentContent, 'insert-characters'));

      // Set focus to end of content
      const newEditorState = EditorState.moveFocusToEnd(
        EditorState.push(titleEditorState, currentContent, 'insert-characters')
      );
      setTitleEditorState(newEditorState);
    }

    setIsMounted(true);
  }, [selectedProject]);

  const onOpenChange = useCallback((_open) => {
    setOpen(_open);
  }, []);
  
  const onSearchChange = useCallback(({ trigger, value }) => {
    if (trigger === '@' && !value) {
      setSuggestions(mentions);
    } else if (trigger === '@') {
      let result = defaultSuggestionsFilter(value, mentions, trigger);
      result = result.sort((a, b) => getScore(a.name, value) - getScore(b.name, value));
      result = result.length > 0 ? result : [{name: value, new: true, trigger}];
      setSuggestions(result);
    }
  }, []);

  const getMentions = (editorState) => {
    const contentState = editorState.getCurrentContent();
    const rawContent = convertToRaw(contentState);

    // Extract mentions from entityMap
    const mentions = Object.values(rawContent.entityMap)
      .filter((entity) => entity.type === 'mention')
      .map((entity) => entity.data.mention);

    return mentions; 
  };

  const adjustToolbarPosition = () => {
    const scrollTop = onHide !== null ? 0 : document?.getElementById('meeting-form')?.scrollTop ?? document?.getElementById('tasks-list-wrapper')?.scrollTop ?? 0;
    document.querySelectorAll('.tukdd6b').forEach(element => {
      element.style.marginTop = `${scrollTop}px`
    })
  }

  // Detect link click
  const handleLinkClick = useCallback((e, editorState, editorKey) => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const block = contentState.getBlockForKey(selection.getStartKey());
    const entityKey = block.getEntityAt(selection.getStartOffset());
    const blockKey = selection.getStartKey();
    // Get entity ranges for the block
    const entityRanges = [];
    block.findEntityRanges(
      (character) => character.getEntity() !== null,
      (start, end) => {
        entityRanges.push([start, end]);
      }
    );

    if (entityKey !== null) {
      const entity = contentState.getEntity(entityKey);
      if (entity.getType() === 'LINK') {
        const linkData = entity.getData();
        const rect = e.target.getBoundingClientRect();
        const editorRect = e.target?.closest('.DraftEditor-root')?.getBoundingClientRect() ?? null;
        editorRect && setPopup({
          url: linkData.url,
          top: rect.top - rect.height - 5 - editorRect.top,
          left: rect.left + rect.width - editorRect.left,
          editorKey,
          entityKey,
          blockKey,
          entityRanges
        });
      }
    }
  }, []);

  // Update link URL
  const handleLinkUpdate = useCallback((e, editorState, setEditorState, isEnter = false) => {
    if (isEnter) { 
      e.preventDefault();
      e.stopPropagation();
      let newUrl = popup.url.trim();
      if (newUrl.length > 0) {
        newUrl = newUrl.startsWith('http://') || newUrl.startsWith('https://') ? newUrl : `https://${newUrl}`;
        const contentState = editorState.getCurrentContent();
        const updatedContentState = contentState.mergeEntityData(popup.entityKey, { url: newUrl });
        setEditorState(EditorState.push(editorState, updatedContentState, 'apply-entity'));
        setPopup(null);
      }
    }
  }, [popup]);

  // Close popup
  const closePopup = (e, editorState, setEditorState) => {
    e.preventDefault();
    e.stopPropagation();
    const {blockKey, entityRanges} = popup;

    if (blockKey && entityRanges) {
      const contentState = editorState.getCurrentContent();
      const selectionState = SelectionState.createEmpty(blockKey).merge({
        anchorOffset: entityRanges[0][0],
        focusOffset: entityRanges[0][1],
      });
      
      // Apply a null entity to remove the link
      const updatedContentState = Modifier.applyEntity(contentState, selectionState, null);

      // Push the updated content state to the editor
      const newEditorState = EditorState.push(editorState, updatedContentState, 'apply-entity');
      setEditorState(newEditorState);
    }
    setPopup(null);
  };

  const handleTitleKeyCommand = (command) => {
    if (command === 'bold') {
      setTitleEditorState(RichUtils.toggleInlineStyle(titleEditorState, 'BOLD'));
      return 'handled';
    }
    if (command === 'italic') {
      setTitleEditorState(RichUtils.toggleInlineStyle(titleEditorState, 'ITALIC'));
      return 'handled';
    }
    if (command === 'underline') {
      setTitleEditorState(RichUtils.toggleInlineStyle(titleEditorState, 'UNDERLINE'));
      return 'handled';
    }
    return 'not-handled';
  };

  const handleDescriptionKeyCommand = (command) => {
    if (command === 'bold') {
      setDescriptionEditorState(RichUtils.toggleInlineStyle(descriptionEditorState, 'BOLD'));
      return 'handled';
    }
    if (command === 'italic') {
      setDescriptionEditorState(RichUtils.toggleInlineStyle(descriptionEditorState, 'ITALIC'));
      return 'handled';
    }
    if (command === 'underline') {
      setDescriptionEditorState(RichUtils.toggleInlineStyle(descriptionEditorState, 'UNDERLINE'));
      return 'handled';
    }
    if (command === 'ordered-list') {
      setDescriptionEditorState(RichUtils.toggleBlockType(descriptionEditorState, 'ordered-list-item'));
      return 'handled';
    }
    if (command === 'unordered-list') {
      setDescriptionEditorState(RichUtils.toggleBlockType(descriptionEditorState, 'unordered-list-item'));
      return 'handled';
    }
    return 'not-handled';
  };

  return (
    <div className='flex flex-col gap-[10px] task-formatting' css={{
      '.public-DraftEditor-content': {
        a: {
          color: `var(--accent-color)`
        }
      },
      '.mentionSuggestionsEntryFocused': {
        backgroundColor: `var(--accent-color)`
      }
    }}>
        <div
          css={{
            position: 'relative',
            '.DraftEditor-editorContainer':{
              fontWeight: '400'
            }
          }}
          className="text-base"
          onKeyDown={e => {
            // Don't handle keypress if input is focused or suggestions are open
            e.target.nodeName !== 'INPUT' && !open && handleKeyPress(e)
          }}
          onClick={e => handleLinkClick(e, titleEditorState, 'editorTitle')}
          onFocus={() => {
            addTaskOnupComingFocus && !setFocus && (currentPage == "upComing" ? addTaskOnupComingFocus(onDateChage) : addTaskOnFocus())
          }}
        >
          <div className="editor-placeholder" css={{display: hasChildTextNode(titleEditorState.getCurrentContent().getPlainText()) ? 'none' : 'flex', fontWeight: '400'}}>Task name</div>
          <Editor
            editorKey={'editorTitle'}
            editorState={titleEditorState}
            handleKeyCommand={handleTitleKeyCommand}
            onChange={(editorState) => {
              try{
                setTitleEditorState(editorState);
                isMounted && onChange(getMentions(editorState));
              } catch (error) {}
            }}
            plugins={plugins}
            ref={titleRef}
          />
          {popup && popup.editorKey === 'editorTitle' && (
            <div className='edit-link-popup' css={{top: popup.top, left: popup.left}}>
              <label>
                <input
                  type="text"
                  autoFocus
                  value={popup.url}
                  onInput={e => setPopup({...popup, url: e.target.value})}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={e => handleLinkUpdate(e, titleEditorState, setTitleEditorState, e.key === 'Enter')}
                />
              </label>
              {popup.url?.trim().length > 0 && <a href="#" onClick={e => handleLinkUpdate(e, titleEditorState, setTitleEditorState, true)}><Check /></a>}
              <a href="#" onClick={e => closePopup(e, titleEditorState, setTitleEditorState)}><Trash /></a>
            </div>
          )}
          <InlineToolbar>
            {
              // may be use React.Fragment instead of div to improve perfomance after React 16
              (externalProps) => {
                adjustToolbarPosition();
                return (
                  <div className='tukdd6b-inner'>
                    <BoldButton {...externalProps} />
                    <ItalicButton {...externalProps} />
                    <UnderlineButton {...externalProps} />
                    <linkPlugin.LinkButton {...externalProps} />
                  </div>
                )
              }
            }
          </InlineToolbar>
          <MentionSuggestions
            open={open}
            onOpenChange={onOpenChange}
            suggestions={suggestions}
            onSearchChange={onSearchChange}
            entryComponent={Entry}
          />  
        </div>

        <div
          css={{
            position: 'relative',
            '.DraftEditor-editorContainer':{
              opacity: 0.7
            }
          }}
          className="text-sm"
          onClick={e => handleLinkClick(e, descriptionEditorState, 'editorDescription')}
          onKeyDown={e => {
            // Don't handle keypress if input is focused or suggestions are open
            e.target.nodeName !== 'INPUT' && handleKeyPress(e)
          }}
        >
          <div
              className="editor-placeholder text-sm"
              css={{
                display: hasChildTextNode(descriptionEditorState.getCurrentContent().getPlainText(), descriptionEditorState.getCurrentContent().getFirstBlock().getType()) ? 'none' : 'flex'
              }}
            >
            Description
          </div>
          <Editor
            editorKey={'editorDescription'}
            editorState={descriptionEditorState}
            handleKeyCommand={handleDescriptionKeyCommand}
            onChange={(editorState) => {
              setDescriptionEditorState(editorState);
            }}
            plugins={descriptionPlugins}
            ref={descriptionRef}
          />
          {popup && popup.editorKey === 'editorDescription' && (
            <div className='edit-link-popup' css={{top: popup.top, left: popup.left}}>
              <label>
                <input
                  type="text"
                  autoFocus
                  value={popup.url}
                  onInput={e => setPopup({...popup, url: e.target.value})}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={e => handleLinkUpdate(e, descriptionEditorState, setDescriptionEditorState, e.key === 'Enter')}
                />
              </label>
              {popup.url?.trim().length > 0 && <a href="#" onClick={e => handleLinkUpdate(e, descriptionEditorState, setDescriptionEditorState, true)}><Check /></a>}
              <a href="#" onClick={e => closePopup(e, descriptionEditorState, setDescriptionEditorState)}><Trash /></a>
            </div>
          )}
          <DescriptionInlineToolbar>
            {
              // may be use React.Fragment instead of div to improve perfomance after React 16
              (externalProps) => {
                adjustToolbarPosition();
                return (
                  <div className='tukdd6b-inner'>
                    <BoldButton {...externalProps} />
                    <ItalicButton {...externalProps} />
                    <UnderlineButton {...externalProps} />
                    <UnorderedListButton {...externalProps} />
                    <OrderedListButton {...externalProps} />
                    <descriptionLinkPlugin.LinkButton {...externalProps} />
                  </div>
                )
              }
            }
          </DescriptionInlineToolbar>   
        </div>
    </div>
  );
});

export default MediumLikeEditorWithMentions;